import React, { useEffect, useState, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, InputBase } from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import CashIcon from "@material-ui/icons/AttachMoney";
import RobotIcon from "@material-ui/icons/EmojiObjects";
import ChatIcon from "@material-ui/icons/ChatBubbleOutline";
import SearchIcon from "@material-ui/icons/Search";
import { useHistory } from "react-router-dom";
import { useVoice } from "../../context/Voice/VoiceContext";
import * as apiVoice from "../../services/apiVoice";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  search: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f6faf7",
    borderRadius: 8,
    padding: "4px 9px",
    marginBottom: 8,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 },
  chip: {
    fontSize: 11,
    padding: "3px 9px",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    color: theme.palette.text.secondary,
  },
  chipOn: {
    background: "#0f6e56",
    color: "#fff",
    borderColor: "#0f6e56",
  },
  ev: { display: "flex", gap: 9, paddingBottom: 12 },
  dot: {
    flex: "none",
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  body: { flex: 1, minWidth: 0 },
  head: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, fontWeight: 500 },
  time: { fontSize: 11, color: theme.palette.text.secondary, fontWeight: 400, whiteSpace: "nowrap" },
  preview: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  empty: { fontSize: 12, color: theme.palette.text.secondary },
}));

const TYPE_META = {
  ticket: { color: "#639922", Icon: ChatIcon },
  call: { color: "#1d9e75", Icon: PhoneIcon },
  payment: { color: "#ba7517", Icon: CashIcon },
  ai_memory: { color: "#7f77dd", Icon: RobotIcon },
};

const FILTERS = [
  { key: "all", labelKey: "voice.section.timeline" },
  { key: "ticket", labelKey: "voice.section.calls" }, // shown as conversations
  { key: "call", labelKey: "voice.section.calls" },
  { key: "payment", labelKey: "voice.section.collection" },
  { key: "ai_memory", labelKey: "voice.section.memory" },
];

const CommunicationTimeline = ({ contactId }) => {
  const classes = useStyles();
  const history = useHistory();
  const { status } = useVoice();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const data = await apiVoice.getCommunicationTimeline(contactId);
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      /* 403 when disabled */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (status && status.enabled) load();
  }, [status, load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (filter !== "all" && e.eventType !== filter) return false;
      if (!q) return true;
      return (
        (e.title || "").toLowerCase().includes(q) ||
        (e.preview || "").toLowerCase().includes(q)
      );
    });
  }, [events, filter, query]);

  if (!status || !status.enabled) return null;

  const openEvent = (e) => {
    if (e.eventType === "ticket" && e.meta && e.meta.uuid) {
      history.push(`/tickets/${e.meta.uuid}`);
    }
  };

  return (
    <div>
      <div className={classes.search}>
        <SearchIcon fontSize="small" style={{ opacity: 0.6 }} />
        <InputBase
          placeholder={i18n.t("voice.timeline.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, fontSize: 13 }}
        />
      </div>
      <div className={classes.chips}>
        {FILTERS.filter((f, i, arr) => arr.findIndex((x) => x.key === f.key) === i).map((f) => (
          <span
            key={f.key}
            className={`${classes.chip} ${filter === f.key ? classes.chipOn : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {i18n.t(f.labelKey)}
          </span>
        ))}
      </div>

      {loading && !events.length ? (
        <CircularProgress size={18} />
      ) : shown.length ? (
        shown.map((e) => {
          const meta = TYPE_META[e.eventType] || TYPE_META.ticket;
          const Icon = meta.Icon;
          return (
            <div
              key={e.id}
              className={classes.ev}
              style={{ cursor: e.eventType === "ticket" ? "pointer" : "default" }}
              onClick={() => openEvent(e)}
            >
              <span className={classes.dot} style={{ background: meta.color }}>
                <Icon style={{ fontSize: 15 }} />
              </span>
              <div className={classes.body}>
                <div className={classes.head}>
                  <span>{e.title}</span>
                  <span className={classes.time}>
                    {new Date(e.occurredAt).toLocaleDateString("he-IL")}
                  </span>
                </div>
                {e.preview ? <div className={classes.preview}>{e.preview}</div> : null}
              </div>
            </div>
          );
        })
      ) : (
        <div className={classes.empty}>{i18n.t("voice.calls.empty")}</div>
      )}
    </div>
  );
};

export default CommunicationTimeline;
